import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Clock, FileText, DollarSign, XCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/types/job';
import { CancellationHistoryData, CancellationTimelineEvent, CancellationDocument } from '@/lib/types/cancellation';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';

interface CancellationHistoryProps {
  data: CancellationHistoryData;
}

export const CancellationHistory = ({ data }: CancellationHistoryProps) => {
  const { request, purchaseOrder } = data;

  // Build timeline from request data
  const timeline: CancellationTimelineEvent[] = [];

  // Initial request
  timeline.push({
    timestamp: request.created_at,
    actor: 'client',
    action: 'Requested Cancellation',
    details: (request.request_details as any)?.reason || 'No reason provided',
    status: 'pending_vendor_response',
  });

  // Vendor response
  if (request.vendor_response_submitted_at) {
    if (request.cancellation_charge_cents === 0) {
      timeline.push({
        timestamp: request.vendor_response_submitted_at,
        actor: 'vendor',
        action: 'Accepted with Full Refund',
        details: `Vendor agreed to full refund of ${formatCurrency(purchaseOrder.po_amount_cents)}`,
        status: 'client_accepted',
      });
    } else {
      timeline.push({
        timestamp: request.vendor_response_submitted_at,
        actor: 'vendor',
        action: 'Proposed Cancellation Fee',
        details: request.cancellation_fee_explanation,
        status: 'pending_client_approval',
        feeAmount: request.cancellation_charge_cents,
        documents: request.cancellation_supporting_documents as CancellationDocument[] | undefined,
      });
    }
  }

  // Client approval decision
  if (request.client_responded_at && request.client_approval_decision) {
    if (request.client_approval_decision === 'accept') {
      timeline.push({
        timestamp: request.client_responded_at,
        actor: 'client',
        action: 'Accepted Cancellation Fee',
        status: 'client_accepted',
      });
    } else if (request.client_approval_decision === 'dispute') {
      timeline.push({
        timestamp: request.client_responded_at,
        actor: 'client',
        action: 'Disputed Cancellation Fee',
        details: 'Client disputed the proposed fee. Dispute created for admin review.',
        status: 'disputed',
      });
    }
  }

  const getStatusIcon = (actor: string, status?: string) => {
    if (status === 'disputed') return <XCircle className="h-5 w-5 text-destructive" />;
    if (status === 'client_accepted' || status === 'completed') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (actor === 'system') return <Clock className="h-5 w-5 text-muted-foreground" />;
    return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      pending_vendor_response: { variant: 'secondary', label: 'Awaiting Vendor' },
      pending_client_approval: { variant: 'secondary', label: 'Awaiting Client' },
      client_accepted: { variant: 'default', label: 'Accepted' },
      disputed: { variant: 'destructive', label: 'Disputed' },
      completed: { variant: 'default', label: 'Completed' },
    };

    const config = variants[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Cancellation Timeline</CardTitle>
          {getStatusBadge(request.cancellation_status)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {timeline.map((event, index) => (
            <div key={index} className="relative pl-8 pb-6 border-l-2 border-border last:border-l-0 last:pb-0">
              <div className="absolute left-0 top-0 -translate-x-1/2 bg-background p-1">
                {getStatusIcon(event.actor, event.status)}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-sm">{event.action}</h4>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(event.timestamp), 'PPp')}
                    </p>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {event.actor}
                  </Badge>
                </div>

                {event.details && (
                  <p className="text-sm text-muted-foreground">{event.details}</p>
                )}

                {event.feeAmount !== undefined && event.feeAmount > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Proposed Fee: {formatCurrency(event.feeAmount)}</span>
                    <span className="text-muted-foreground">
                      (Refund: {formatCurrency(purchaseOrder.po_amount_cents - event.feeAmount)})
                    </span>
                  </div>
                )}

                {event.documents && event.documents.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Supporting Documents:</p>
                    <div className="space-y-1">
                      {event.documents.map((doc, docIndex) => (
                        <Button
                          key={docIndex}
                          variant="ghost"
                          size="sm"
                          className="h-auto py-1 px-2 justify-start"
                          asChild
                        >
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                            <FileText className="h-3 w-3 mr-2" />
                            <span className="text-xs">{doc.file_name}</span>
                          </a>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
